import React from 'react';
import { observer } from 'mobx-react';
import { observable, computed } from 'mobx';
import PropTypes from 'prop-types';
import { Api } from '@seedlang/stores';
import pluralize from 'pluralize';
import { UrlBuilder } from '@seedlang/models';
import { isPresent } from '@seedlang/utils';
import Spinner from 'components/spinner';
import styled from '@emotion/styled';
import Alert from 'components/alert';
import imageCompression from 'browser-image-compression';
import withLegend from 'components/form/with_legend';
import DeleteUploadBtn from 'components/form/delete_upload_btn';

const Wrapper = styled.div`
  background: white;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  input {
    margin: ${props => props.margin};
  }
`;

const CircleImage = styled.img`
  object-fit: cover;
  object-position: center center;
  width: 150px;
  height: 150px;
  border-radius: 50%;
`;

const ImageWrapper = styled.div`
  position: relative;
  display: inline-block;
  .fa-times {
    position: absolute;
    right: 0;
    background: red;
    color: white;
    width: 25px;
    height: 25px;
    text-align: center;
    line-height: 24px;
    border-radius: 50%;
    font-size: 14px;
  }
  img {
    margin-top: 10px;
    max-width: 180px;
  }
`;

@observer
class InPlaceImageUpload extends React.Component {
  constructor(props) {
    super(props);
    this.inputRef = React.createRef();
  }

  @observable showSpinner = false;
  @observable imageUrlFromResponse;
  @observable error = null;
  activeUpload = null;
  static propTypes = {
    onChange: PropTypes.func,
    value: PropTypes.string,
    model: PropTypes.string,
    uploadUrl: PropTypes.string,
    querystring: PropTypes.object,
    margin: PropTypes.string,
  };

  static defaultProps = {
    margin: '0 0 20px 0',
  };

  @computed get imageUrl() {
    if (this.props.image) {
      return this.props.image.url;
    } if (this.imageUrlFromResponse) {
      return this.imageUrlFromResponse;
    }
    return null;
  }
  onMultipartPost = async () => {
    const file = this.inputRef.current?.files?.[0];
    if (!file) return;

    this.cancelUpload();
    this.showSpinner = false;

    const MAX_FILE_SIZE = 6 * 1024 * 1024; // 6MB in bytes
    const MAX_COMPRESSED_SIZE = 1024 * 1024; // 1MB in bytes

    if (file.size > MAX_FILE_SIZE) {
      this.error = 'File size exceeds 6MB limit. Please choose a smaller file.';
      this.clearFileInput();
      return;
    }

    this.error = null;
    this.showSpinner = true;
    const upload = { controller: new AbortController() };
    this.activeUpload = upload;
    const finishUpload = callback => resp => {
      if (this.activeUpload !== upload) return;
      this.activeUpload = null;
      callback(resp);
    };

    try {
      let fileToUpload = file;

      if (file.size > MAX_COMPRESSED_SIZE) {
        const options = {
          maxSizeMB: 1,
          maxWidthOrHeight: 1920,
          useWebWorker: true,
          signal: upload.controller.signal,
        };
        const compressedBlob = await imageCompression(file, options);

        // Create a new File object from the compressed blob to maintain metadata
        fileToUpload = new File([compressedBlob], file.name, {
          type: file.type,
          lastModified: file.lastModified,
        });
      }

      if (this.activeUpload !== upload) return;

      upload.request = Api.multipartPost({
        url: this.props.uploadUrl || new UrlBuilder().build(pluralize(this.props.model), this.props.querystring),
        data: { data: this.data(fileToUpload) },
        successCallback: finishUpload(this.onChange),
        failureCallback: finishUpload(this.onUploadError),
        onRequestError: finishUpload(this.onUploadError),
      });
    } catch {
      if (this.activeUpload !== upload) return;
      this.activeUpload = null;
      this.error = 'Error processing image. Please try again.';
      this.showSpinner = false;
      this.clearFileInput();
    }
  }

  cancelUpload = () => {
    const upload = this.activeUpload;
    this.activeUpload = null;
    upload?.controller.abort();
    upload?.request?.abort();
  }

  componentWillUnmount() {
    this.cancelUpload();
  }

  onUploadError = () => {
    this.error = 'Image upload failed. Please try again.';
    this.showSpinner = false;
    this.clearFileInput();
  }

  clearFileInput = () => {
    if (this.inputRef.current) {
      this.inputRef.current.value = '';
    }
  }

  data(file = this.inputRef.current?.files?.[0]) {
    const data = new FormData();
    data.append('blob', file);
    return data;
  }
  onDelete = () => {
    const id = this.props.id ?? this.props.image?.id;
    if (!id) {
      this.imageUrlFromResponse = null;
      this.onChange({ success: true });
      return;
    }
    this.showSpinner = true;
    this.imageUrlFromResponse = null;
    Api.destroy({
      url: `/api/${pluralize(this.props.model)}/${id}`,
      successCallback: this.onChange,
    });
  }
  onChange = (resp) => {
    if (!resp.success) {
      this.onUploadError();
      return;
    }
    this.showSpinner = false;
    if (resp.success) {
      if (resp.url) {
        this.imageUrlFromResponse = resp.url;
      }
      if (this.props.updateCallback) {
        this.props.updateCallback(resp);
      }
    }

    // Clear the file input so that if the user selects the same file again, the input's onChange event will fire
    this.clearFileInput();

    if (this.props.onChange) {
      this.props.onChange(resp);
    }
  }

  render() {
    return (
      <Wrapper
        margin={this.props.margin}
      >
        {this.error && (
          <Alert
            margin='0 0 10px 0'
          >
            {this.error}
          </Alert>
        )}
        <input
          type='file'
          ref={this.inputRef}
          onChange={this.onMultipartPost}
          accept='.jpg,.jpeg,.png,.gif'
          style={{ display: isPresent(this.imageUrl) ? 'none' : 'block' }}
        />
        {
          isPresent(this.imageUrl) &&
            <ImageWrapper>
              {
                !this.props.hideDelete &&
                  <DeleteUploadBtn
                    i='times'
                    onClick={this.onDelete.bind(this)}
                  />
              }
              {
                !this.props.circle && <img alt='' src={this.imageUrl} />
              }
              {
                this.props.circle &&
                  <CircleImage
                    alt=''
                    src={this.imageUrl}
                    onClick={this.onClick}
                  />
              }
            </ImageWrapper>
        }
        {
          this.showSpinner &&
            <Spinner
              className='blue'
              margin='0 auto'
            />
        }
      </Wrapper>
    );
  }
}

export default withLegend(InPlaceImageUpload);
